import { useSyncExternalStore } from "react";

/**
 * How much of the viewport the expanded selection inspector covers, px,
 * measured in from the bottom edge (bottom sheet) or the right edge (the
 * landscape side sheet). Zero while it's collapsed to its bar or closed.
 *
 * Canvases read it to glide the selection out from under the sheet; it's
 * also mirrored to `--inspector-h` / `--inspector-w` on `<html>` for CSS.
 */
export interface InspectorInset {
  bottom: number;
  right: number;
}

const NONE: InspectorInset = { bottom: 0, right: 0 };
let current: InspectorInset = NONE;
const listeners = new Set<() => void>();

export function publishInspectorInset(next: InspectorInset) {
  const bottom = Math.max(0, Math.round(next.bottom));
  const right = Math.max(0, Math.round(next.right));
  if (bottom === current.bottom && right === current.right) return;
  current = bottom === 0 && right === 0 ? NONE : { bottom, right };
  if (typeof document !== "undefined") {
    const root = document.documentElement.style;
    root.setProperty("--inspector-h", `${bottom}px`);
    root.setProperty("--inspector-w", `${right}px`);
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const snapshot = () => current;

export function useInspectorInset(): InspectorInset {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
